import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Accordion,
  Alert,
  Badge,
  Breadcrumb,
  Button,
  Card,
  Container,
  Table,
} from "react-bootstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faServer, faSync } from "@fortawesome/free-solid-svg-icons";
import { SERVER_DOCUMENT } from "./serverInventory.js";
import useLayout from "../../hooks/useLayout.js";
import Loader from "../../components/Loader.js";

const USD_TO_BRL = 5.15;

const COST_SUMMARY = [
  {
    name: "Servidor de Produção",
    weekly: 15.07,
    monthly: 65.51,
  },
  {
    name: "Servidor de Banco de Dados",
    weekly: 6.85,
    monthly: 29.77,
  },
  {
    name: "Servidor do Credenciamento",
    weekly: 7.04,
    monthly: 30.58,
  },
  {
    name: "Servidor de Projetos e Websites",
    weekly: 7.22,
    monthly: 31.38,
  },
  {
    name: "Servidor de Desenvolvimento e Testes — uso eventual",
    weekly: 4.63,
    monthly: 18.52,
  },
];

const CONSOLIDATED_TOTALS = [
  {
    label: "Servidores ativos, sem desenvolvimento",
    weekly: 36.18,
    monthly: 157.24,
  },
  {
    label: "Todos ligados continuamente",
    weekly: 49.78,
    monthly: 216.35,
  },
  {
    label: "Desenvolvimento em uso eventual",
    weekly: 40.81,
    monthly: 175.76,
  },
];

const formatBrl = (usdValue) => (usdValue * USD_TO_BRL).toLocaleString("pt-BR", {
  style: "currency",
  currency: "BRL",
});

const USD_AMOUNT_PATTERN = /US\$\s*([0-9]+(?:[.,][0-9]+)?)/g;

const convertUsdTextToBrl = (value) => value.replace(USD_AMOUNT_PATTERN, (_, amount) => {
  const normalizedAmount = amount.includes(",")
    ? amount.replace(/\./g, "").replace(",", ".")
    : amount;
  const numericAmount = Number(normalizedAmount);
  return Number.isFinite(numericAmount) ? formatBrl(numericAmount) : `US$ ${amount}`;
});

const SERVER_METADATA = [
  { id: "projects", title: "Servidor de Projetos e Websites", status: "Ativo", variant: "success" },
  { id: "dev", title: "Servidor de Desenvolvimento e Testes", status: "Uso eventual", variant: "warning" },
  { id: "credentialing", title: "Servidor do Credenciamento", status: "Ativo", variant: "success" },
  { id: "platform", title: "Servidor de Produção", status: "Ativo", variant: "success" },
  { id: "database", title: "Servidor de Banco de Dados", status: "Ativo", variant: "success" },
];

const SERVER_SECTION_ORDER = [
  "Servidor de Produção",
  "Servidor de Banco de Dados",
  "Servidor do Credenciamento",
  "Servidor de Projetos e Websites",
  "Servidor de Desenvolvimento e Testes",
];

const LIVE_STATUS_LABELS = {
  loading: "Carregando...",
  online: "Online",
  offline: "Offline",
  degraded: "Degradado",
  not_configured: "Não configurado",
  unavailable: "Indisponível",
  valid: "Válido",
  expired: "Expirado",
  not_yet_valid: "Ainda não válido",
  hostname_mismatch: "Hostname inválido",
  untrusted: "Não confiável",
  invalid: "Inválido",
  timeout: "Tempo esgotado",
};

const LIVE_STATUS_VARIANTS = {
  loading: "secondary",
  online: "success",
  offline: "danger",
  degraded: "warning",
  not_configured: "secondary",
  unavailable: "secondary",
  valid: "success",
  expired: "danger",
  not_yet_valid: "danger",
  hostname_mismatch: "danger",
  untrusted: "danger",
  invalid: "danger",
  timeout: "warning",
};

const SERVER_NAME_ALIASES = {
  platform: ["servidor principal", "servidor de produção", "servidor de plataformas"],
  credentialing: ["servidor de credenciamento"],
  projects: ["servidor projetos", "servidor de projetos", "servidor de projetos e websites"],
  database: ["servidor do banco de dados", "servidor de banco de dados"],
  dev: ["servidor dev", "servidor de desenvolvimento e testes"],
};

const METRIC_DEFINITIONS = [
  { key: "cpu_percent", label: "CPU", threshold: 80, redWhenAbove: true, inclusive: true },
  { key: "memory_percent", label: "Memória", threshold: 80, redWhenAbove: true, inclusive: true },
  { key: "disk_percent", label: "Disco", threshold: 85, redWhenAbove: true, inclusive: true },
  { key: "swap_percent", label: "Swap", threshold: 5, redWhenAbove: true },
];

const SERVER_TITLE_PATTERN = /^(?:#\s*)?Servidor (?:de|do) (?:Projetos e Websites|Desenvolvimento e Testes|Credenciamento|Produção|Banco de Dados)$/;
const CODE_MARK = String.fromCharCode(96);
const INLINE_TOKEN_PATTERN = new RegExp("(\\*\\*[^*]+\\*\\*|" + CODE_MARK + "[^" + CODE_MARK + "]+" + CODE_MARK + ")", "g");

const renderInline = (value) => value.split(INLINE_TOKEN_PATTERN).map((part, index) => {
  if (part.startsWith("**") && part.endsWith("**")) {
    return <strong key={`bold-${index}`}>{convertUsdTextToBrl(part.slice(2, -2))}</strong>;
  }

  if (part.startsWith(CODE_MARK) && part.endsWith(CODE_MARK)) {
    return <code key={`code-${index}`}>{convertUsdTextToBrl(part.slice(1, -1))}</code>;
  }

  return <React.Fragment key={`text-${index}`}>{convertUsdTextToBrl(part)}</React.Fragment>;
});

const parseTableRow = (line) => line
  .trim()
  .replace(/^\|/, "")
  .replace(/\|$/, "")
  .split("|")
  .map((cell) => cell.trim());

const isTableSeparator = (line) => /^\|?\s*:?-{3,}:?\s*(\|\s*:?-{3,}:?\s*)+\|?$/.test(line.trim());

const isBlockStart = (line, nextLine = "") => (
  /^#{1,4}\s/.test(line) ||
  /^>\s?/.test(line) ||
  /^-\s/.test(line) ||
  /^\d+\.\s/.test(line) ||
  /^-{3,}$/.test(line.trim()) ||
  (line.trim().startsWith("|") && isTableSeparator(nextLine))
);

const MarkdownDocument = ({ content }) => {
  const lines = content.split(/\r?\n/);
  const blocks = [];
  let index = 0;

  while (index < lines.length) {
    const line = lines[index];

    if (!line.trim()) {
      index += 1;
      continue;
    }

    if (/^-{3,}$/.test(line.trim())) {
      blocks.push(<hr key={`rule-${index}`} />);
      index += 1;
      continue;
    }

    const headingMatch = line.match(/^(#{1,4})\s+(.+)$/);
    if (headingMatch) {
      const Heading = headingMatch[1].length === 1
        ? "h2"
        : headingMatch[1].length === 2
          ? "h4"
          : "h5";
      blocks.push(
        <Heading key={`heading-${index}`} className="mt-4 mb-2">
          {renderInline(headingMatch[2])}
        </Heading>
      );
      index += 1;
      continue;
    }

    if (line.trim().startsWith("|") && isTableSeparator(lines[index + 1] || "")) {
      const headers = parseTableRow(line);
      const rows = [];
      index += 2;

      while (index < lines.length && lines[index].trim().startsWith("|")) {
        rows.push(parseTableRow(lines[index]));
        index += 1;
      }

      blocks.push(
        <div className="table-responsive mb-4" key={`table-${index}`}>
          <Table bordered hover size="sm" className="mb-0 align-middle">
            <thead>
              <tr>
                {headers.map((header, headerIndex) => (
                  <th key={`header-${headerIndex}`} scope="col">
                    {renderInline(header)}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, rowIndex) => (
                <tr key={`row-${rowIndex}`}>
                  {headers.map((_, cellIndex) => (
                    <td key={`cell-${rowIndex}-${cellIndex}`}>
                      {renderInline(row[cellIndex] || "")}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
      );
      continue;
    }

    if (/^>\s?/.test(line)) {
      const quoteLines = [];
      while (index < lines.length && /^>\s?/.test(lines[index])) {
        quoteLines.push(lines[index].replace(/^>\s?/, ""));
        index += 1;
      }

      blocks.push(
        <div className="border-start border-3 ps-3 mb-3 text-muted" key={`quote-${index}`}>
          {quoteLines.map((quote, quoteIndex) => (
            <div key={`quote-line-${quoteIndex}`}>{renderInline(quote)}</div>
          ))}
        </div>
      );
      continue;
    }

    if (/^-\s/.test(line) || /^\d+\.\s/.test(line)) {
      const ordered = /^\d+\.\s/.test(line);
      const List = ordered ? "ol" : "ul";
      const items = [];

      while (index < lines.length) {
        const itemMatch = lines[index].match(ordered ? /^\d+\.\s(.+)$/ : /^-\s(.+)$/);
        if (!itemMatch) break;
        items.push(itemMatch[1]);
        index += 1;
      }

      blocks.push(
        <List key={`list-${index}`} className="mb-3">
          {items.map((item, itemIndex) => (
            <li key={`item-${itemIndex}`}>{renderInline(item)}</li>
          ))}
        </List>
      );
      continue;
    }

    const paragraphLines = [line];
    index += 1;
    while (
      index < lines.length &&
      lines[index].trim() &&
      !isBlockStart(lines[index], lines[index + 1] || "")
    ) {
      paragraphLines.push(lines[index]);
      index += 1;
    }

    blocks.push(
      <p key={`paragraph-${index}`} className="mb-3">
        {renderInline(paragraphLines.join(" "))}
      </p>
    );
  }

  return <>{blocks}</>;
};

const getServerSections = (document) => {
  const lines = document.split(/\r?\n/);
  const starts = lines.reduce((result, line, index) => (
    SERVER_TITLE_PATTERN.test(line.trim()) ? [...result, index] : result
  ), []);

  return starts.map((start, sectionIndex) => {
    const end = starts[sectionIndex + 1] || lines.length;
    return {
      title: lines[start].replace(/^#\s*/, "").trim(),
      content: lines.slice(start + 1, end).join("\n"),
    };
  });
};

const normalizeLiveStatus = (status) => (
  Object.prototype.hasOwnProperty.call(LIVE_STATUS_LABELS, status) ? status : "unavailable"
);

const StatusBadge = ({ status }) => {
  const normalizedStatus = normalizeLiveStatus(status);
  return (
    <Badge bg={LIVE_STATUS_VARIANTS[normalizedStatus]}>
      {LIVE_STATUS_LABELS[normalizedStatus]}
    </Badge>
  );
};

const findLiveServer = (payload, serverId) => {
  const servers = Array.isArray(payload?.servers) ? payload.servers : [];
  const aliases = SERVER_NAME_ALIASES[serverId] || [];
  return servers.find((server) => {
    const id = String(server?.id || server?.server_id || server?.serverId || "").trim().toLowerCase();
    const name = String(server?.name || "").trim().toLowerCase();
    return id === serverId || aliases.includes(name);
  }) || null;
};

const formatLiveDate = (value) => {
  if (!value) return "N/D";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/D";
  return new Intl.DateTimeFormat("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date).replace(/\s*,\s*/, " ");
};

const formatPercent = (value) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "N/D";
  return `${number.toLocaleString("pt-BR", { maximumFractionDigits: 1 })}%`;
};

const formatBytes = (value) => {
  const bytes = Number(value);
  if (!Number.isFinite(bytes) || bytes < 0) return "N/D";
  if (bytes === 0) return "0 Bytes";
  const sizes = ["Bytes", "KB", "MB", "GB", "TB"];
  const index = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1);
  return `${parseFloat((bytes / (1024 ** index)).toFixed(2)).toLocaleString("pt-BR")} ${sizes[index]}`;
};

const formatUptime = (value) => {
  const startedAt = Number(value);
  if (!Number.isFinite(startedAt) || startedAt <= 0) return "N/D";
  const seconds = Math.max(0, Math.floor((Date.now() - startedAt) / 1000));
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  return `${Math.floor(hours / 24)}d`;
};

const metricStatus = (value, definition) => {
  const number = Number(value);
  if (!Number.isFinite(number)) return "unavailable";
  const exceeded = definition.redWhenAbove
    ? (definition.inclusive ? number >= definition.threshold : number > definition.threshold)
    : number <= definition.threshold;
  return exceeded ? "offline" : "online";
};

const metricServerStatus = (status) => {
  if (status === "ok") return "online";
  if (status === "degraded") return "degraded";
  return "unavailable";
};

const EmptyLiveState = ({ children }) => (
  <div className="border rounded p-3 text-muted small">{children}</div>
);

const LiveServerDetails = ({
  serverId,
  statusPayload,
  metricsPayload,
  statusError,
  metricsError,
  loading,
  lastUpdated,
}) => {
  const server = findLiveServer(statusPayload, serverId);
  const metricServer = findLiveServer(metricsPayload, serverId);
  const domains = Array.isArray(server?.domains) ? server.domains : [];
  const apis = Array.isArray(server?.apis) ? server.apis : [];
  const metrics = metricServer?.metrics && typeof metricServer.metrics === "object"
    ? metricServer.metrics
    : {};
  const overallStatus = loading ? "loading" : server?.status || (statusError ? "unavailable" : "not_configured");
  const metricOverallStatus = metricServerStatus(metricServer?.status);

  return (
    <div className="mb-4">
      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <h5 className="mb-0">Monitoramento em tempo real</h5>
        {lastUpdated && <span className="text-muted small">Última consulta: {formatLiveDate(lastUpdated)}</span>}
      </div>

      {statusError && (
        <Alert variant="danger" className="p-2">
          Não foi possível carregar o status de domínios e APIs. Os dados estáticos continuam disponíveis.
        </Alert>
      )}
      {metricsError && (
        <Alert variant="danger" className="p-2">
          Não foi possível carregar as métricas do CloudWatch. O estado online/offline não depende dessas métricas.
        </Alert>
      )}

      <div className="border rounded p-3 mb-4 d-flex flex-wrap align-items-center justify-content-between gap-2">
        <span className="small text-muted text-uppercase fw-bold">Status geral do servidor</span>
        <StatusBadge status={overallStatus} />
      </div>

      <h6 className="text-uppercase fw-bold mb-3">Domínios</h6>
      <div className="row g-3 mb-4">
        {domains.length === 0 ? (
          <div className="col-12">
            <EmptyLiveState>
              {loading ? "Carregando dados de domínios..." : statusError ? "Dados de domínios indisponíveis." : "Nenhum domínio configurado para este servidor."}
            </EmptyLiveState>
          </div>
        ) : domains.map((domain) => (
          <div className="col-md-6" key={domain.id || domain.label}>
            <div className="border rounded p-3 h-100">
              <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                <div className="fw-semibold">{domain.label || "Domínio"}</div>
                <StatusBadge status={domain.status} />
              </div>
              <div className="small text-muted text-break">{domain.url || "URL não configurada"}</div>
              <div className="small mt-2">HTTP: {domain.status_code || "N/D"}</div>
              <div className="d-flex flex-wrap align-items-center gap-2 small mt-2">
                <span className="text-muted">Certificado:</span>
                <StatusBadge status={domain.certificate?.status || "unavailable"} />
                {domain.certificate?.valid_to && (
                  <span className="text-muted">até {formatLiveDate(domain.certificate.valid_to)}</span>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>

      {serverId !== "database" && (
        <>
          <h6 className="text-uppercase fw-bold mb-3">APIs</h6>
          <div className="row g-3 mb-4">
            {apis.length === 0 ? (
              <div className="col-12">
                <EmptyLiveState>
                  {loading ? "Carregando dados de APIs..." : statusError ? "Dados de APIs indisponíveis." : "Nenhuma API configurada para este servidor."}
                </EmptyLiveState>
              </div>
            ) : apis.map((api) => {
              const websocket = api.websocket || {};
              const connections = Number.isFinite(Number(websocket.current_connections))
                ? ` (${Number(websocket.current_connections).toLocaleString("pt-BR")} conexões)`
                : "";
              const processes = Array.isArray(api.processes) ? api.processes : [];

              return (
                <div className="col-md-6" key={api.id || api.label}>
                  <div className="border rounded p-3 h-100">
                    <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                      <div className="fw-semibold">{api.label || "API"}</div>
                      <StatusBadge status={api.status} />
                    </div>
                    <div className="small text-muted text-break">{api.health_url || "URL não configurada"}</div>
                    <div className="small mt-2">HTTP: {api.status_code || "N/D"}</div>
                    <div className="small mt-1">Health check: {api.health_status || "N/D"}</div>
                    <div className="small mt-1">Banco de dados: {api.database || "N/D"}</div>
                    <div className="small mt-1">WebSocket: {websocket.status || "N/D"}{connections}</div>
                    <div className="small mt-1">Hostname: {api.hostname || "N/D"}</div>
                    {processes.length > 0 && (
                      <div className="mt-3 pt-2 border-top">
                        <div className="small text-uppercase text-muted fw-bold mb-2">Processos PM2</div>
                        {processes.map((process, index) => (
                          <div className="small mb-2" key={`${process.name || "process"}-${process.pm_id || index}`}>
                            <div className="fw-semibold">{process.name || "Processo"}</div>
                            <div className="text-muted">
                              Status: {process.status || "N/D"} · CPU: {formatPercent(process.cpu)} · Memória: {formatBytes(process.memory)}
                            </div>
                            <div className="text-muted">
                              Restarts: {process.restarts ?? "N/D"} · Uptime: {formatUptime(process.uptime)}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </>
      )}

      <div className="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-3">
        <h6 className="text-uppercase fw-bold mb-0">Métricas do servidor</h6>
        <div className="d-flex align-items-center gap-2 small text-muted">
          <span>CloudWatch: {metricServer?.source || "N/D"}</span>
          <StatusBadge status={metricOverallStatus} />
        </div>
      </div>
      <div className="row g-3">
        {METRIC_DEFINITIONS.map((definition) => (
          <div className="col-6 col-lg-3" key={definition.key}>
            <div className="border rounded p-3 h-100">
              <div className="small text-muted text-uppercase fw-bold mb-1">{definition.label}</div>
              <div className={`fs-5 fw-bold text-${LIVE_STATUS_VARIANTS[metricStatus(metrics[definition.key], definition)]}`}>
                {formatPercent(metrics[definition.key])}
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="small text-muted mt-2">
        Coletado em: {formatLiveDate(metricServer?.collected_at)} · Período: {metricsPayload?.period_seconds || "N/D"} segundos
      </div>
    </div>
  );
};

const Servers = () => {
  const serverSections = getServerSections(SERVER_DOCUMENT).sort(
    (first, second) => SERVER_SECTION_ORDER.indexOf(first.title) - SERVER_SECTION_ORDER.indexOf(second.title)
  );
  const { setLoading } = useLayout();
  const [liveState, setLiveState] = useState({
    loading: true,
    status: null,
    metrics: null,
    statusError: false,
    metricsError: false,
    lastUpdated: null,
  });

  const loadLiveData = useCallback(async ({ showGlobalLoading = false } = {}) => {
    setLiveState((current) => ({ ...current, loading: true }));
    if (showGlobalLoading) setLoading(true);

    const request = (url) => axios.get(url)
      .then((response) => ({ data: response.data, error: false }))
      .catch(() => ({ data: null, error: true }));

    try {
      const [statusResult, metricsResult] = await Promise.all([
        request("/external/ops/servers/status"),
        request("/external/ops/servers/metrics"),
      ]);
      const statusOk = !statusResult.error && statusResult.data?.success !== false;
      const metricsOk = !metricsResult.error && metricsResult.data?.success !== false;

      setLiveState({
        loading: false,
        status: statusOk ? statusResult.data : null,
        metrics: metricsOk ? metricsResult.data : null,
        statusError: !statusOk,
        metricsError: !metricsOk,
        lastUpdated: statusOk || metricsOk ? new Date().toISOString() : null,
      });
    } finally {
      if (showGlobalLoading) setLoading(false);
    }
  }, [setLoading]);

  useEffect(() => {
    loadLiveData();
  }, [loadLiveData]);

  const hasLiveData = Boolean(liveState.status) || Boolean(liveState.metrics);

  if (liveState.loading && !hasLiveData) {
    return <Loader />;
  }

  return (
    <Container fluid className="p-0 container">
      <Button
        type="button"
        variant="primary"
        className="btn update-side-panel-button"
        onClick={() => loadLiveData({ showGlobalLoading: true })}
        disabled={liveState.loading}
        title="Atualizar status e métricas"
        aria-label="Atualizar status e métricas"
      >
        <FontAwesomeIcon icon={faSync} spin={liveState.loading} size="lg" />
      </Button>

      <Breadcrumb>
        <Breadcrumb.Item active>Gerenciamento</Breadcrumb.Item>
        <Breadcrumb.Item active>Servidores</Breadcrumb.Item>
      </Breadcrumb>

      <h6 className="text-muted mb-0">
        Inventário de infraestrutura e estimativas AWS On-Demand em <code>sa-east-1</code>.
      </h6>

      <Card className="mt-3 mb-3">
        <Card.Body className="p-4">
          <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 mb-3">
            <h5 className="mb-0">Resumo de custos</h5>
          </div>
          <div className="text-muted small mb-3">
            <div><strong>Última atualização:</strong> 2026-08-06</div>
            <div><strong>Responsável:</strong> Wellington Lopes</div>
            <div><strong>Status:</strong> Ativo</div>
          </div>
          <div className="text-muted small mb-3">
            Estimativas com preços AWS On-Demand na região <code>sa-east-1</code>, sem incluir tráfego de saída,
            snapshots, armazenamento adicional, logs do CloudWatch, impostos ou descontos.
          </div>
          <Alert variant="primary" className="p-2 mb-3">
            Conversão utilizada: US$ 1,00 = R$ 5,15. Os valores em BRL são referenciais e a taxa não é atualizada automaticamente.
          </Alert>
          <div className="table-responsive">
            <Table striped bordered hover size="sm" className="mb-0 align-middle">
              <thead>
                <tr>
                  <th scope="col">Servidor / cenário</th>
                  <th scope="col" className="text-end">Total semanal</th>
                  <th scope="col" className="text-end">Total mensal aproximado</th>
                </tr>
              </thead>
              <tbody>
                {COST_SUMMARY.map((row) => (
                  <tr key={row.name}>
                    <td>{row.name}</td>
                    <td className="text-end fw-semibold">{formatBrl(row.weekly)}</td>
                    <td className="text-end fw-semibold">{formatBrl(row.monthly)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>

          <h5 className="mt-4 mb-3">Totais consolidados</h5>
          <div className="row g-3 mb-3">
            {CONSOLIDATED_TOTALS.map((total) => (
              <div className="col-sm-6 col-lg-4" key={total.label}>
                <div className="border rounded p-3 h-100">
                  <div className="small text-muted text-uppercase fw-bold mb-1">{total.label}</div>
                  <div className="fs-4 fw-bold text-primary">{formatBrl(total.weekly)} / semana</div>
                  <div className="small text-muted">{formatBrl(total.monthly)} / mês</div>
                </div>
              </div>
            ))}
          </div>

          <Alert variant="primary" className="p-2 mt-3 mb-0">
            O servidor de desenvolvimento não deve ser somado ao cenário base quando estiver desligado;
            nesse caso, permanece principalmente o custo do armazenamento EBS.
          </Alert>
        </Card.Body>
      </Card>

      <div className="textHeaderAplicativo mb-3 col">
        <h5 className="card-title mb-0">Detalhes dos servidores</h5>
      </div>

      <Accordion className="mb-4" defaultActiveKey="0">
        {serverSections.map((section, index) => {
          const metadata = SERVER_METADATA.find((item) => item.title === section.title) || {
            id: section.title,
            status: "Informativo",
            variant: "secondary",
          };
          const liveServer = findLiveServer(liveState.status, metadata.id);
          const liveStatus = liveState.loading
            ? "loading"
            : liveServer?.status || (liveState.statusError ? "unavailable" : "not_configured");

          return (
            <Accordion.Item eventKey={String(index)} key={section.title} className="mb-3 shadow-sm border rounded">
              <Accordion.Header className="card mb-0">
                <div className="d-flex flex-wrap align-items-center justify-content-between gap-3 w-100 pe-2">
                  <div className="d-flex align-items-center gap-3">
                    <div
                      className="rounded bg-primary text-white d-flex align-items-center justify-content-center"
                      style={{ width: 42, height: 42 }}
                      aria-hidden="true"
                    >
                      <FontAwesomeIcon icon={faServer} />
                    </div>
                    <div>
                      <h5 className="mb-1">{section.title}</h5>
                      <div className="text-muted small">Ficha de infraestrutura</div>
                    </div>
                  </div>
                  <StatusBadge status={liveStatus} />
                </div>
              </Accordion.Header>
              <Accordion.Body className="card mb-0 p-4">
                <LiveServerDetails
                  serverId={metadata.id}
                  statusPayload={liveState.status}
                  metricsPayload={liveState.metrics}
                  statusError={liveState.statusError}
                  metricsError={liveState.metricsError}
                  loading={liveState.loading}
                  lastUpdated={liveState.lastUpdated}
                />
                <hr className="my-4" />
                <MarkdownDocument content={section.content} />
              </Accordion.Body>
            </Accordion.Item>
          );
        })}
      </Accordion>
    </Container>
  );
};

export default Servers;
